import {useMemo, useState} from "react";
import {SectionDiff} from "../../utils/branchDiff";
import Collapsible from "../../elements/collapsible";
import RecordDiffView from "./recordDiffView";
import {RemoteConstantsModel} from "../../models/remoteConstants.model";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import {classifyMetadataMigration, isMetadataSection} from "../../utils/migrateRecord";
import {MigrateMetadataRecordCommand} from "../../commands/migrateMetadataRecord.command";

interface SectionDiffViewProps {
    section: SectionDiff;
    targetData: RemoteConstantsModel;
}

function SectionDiffView(props: SectionDiffViewProps) {
    const s = props.section;
    const summary = s.identical
        ? 'identical'
        : `+${s.onlyInTarget.length}  -${s.onlyInCurrent.length}  Δ${s.differs.length}`;

    const header = (
        <div className={'layout horizontal center stretch'} style={{width: '100%'}}>
            <p style={{fontWeight: 'bold'}}>{s.title}</p>
            <p style={{color: s.identical ? 'var(--muted-text-color)' : 'white'}}>{summary}</p>
        </div>
    );

    if (s.identical) {
        return (
            <div className={'box'} style={{padding: '6px 12px', opacity: 0.6}}>
                {header}
            </div>
        );
    }

    return (
        <div className={'box'} style={{padding: '6px 12px'}}>
            <Collapsible title={header} defaultOpen={false}>
                <div className={'layout vertical gap'} style={{gap: 8, marginTop: 8}}>
                    {s.duplicateNote && (
                        <p style={{color: '#ffaa66', fontSize: 12, fontStyle: 'italic'}}>
                            ⚠ {s.duplicateNote} — pairing is heuristic
                        </p>
                    )}
                    {s.onlyInTarget.length > 0 && (
                        <div className={'layout vertical'}>
                            <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 13}}>
                                Only in Other ({s.onlyInTarget.length})
                            </p>
                            {s.onlyInTarget.map(r => (
                                <OnlyInOtherRow key={r.identity}
                                                sectionKey={s.sectionKey}
                                                identity={r.identity}
                                                name={r.name}
                                                targetData={props.targetData}/>
                            ))}
                        </div>
                    )}
                    {s.onlyInCurrent.length > 0 && (
                        <div className={'layout vertical'}>
                            <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 13}}>
                                Only in Current ({s.onlyInCurrent.length})
                            </p>
                            {s.onlyInCurrent.map(r => (
                                <p key={r.identity} style={{paddingLeft: 10}}>- {r.name}</p>
                            ))}
                        </div>
                    )}
                    {s.differs.length > 0 && (
                        <div className={'layout vertical'}>
                            <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 13}}>
                                Differs ({s.differs.length})
                            </p>
                            {s.differs.map(r => <DiffersRow key={r.identity} identity={r.identity} name={r.name} fieldChanges={r.fieldChanges} sectionKey={s.sectionKey}/>)}
                        </div>
                    )}
                </div>
            </Collapsible>
        </div>
    );
}

function DiffersRow(props: {
    identity: string;
    name: string;
    fieldChanges: import('../../utils/branchDiff').RecordDiff['fieldChanges'];
    sectionKey: import('../../utils/branchDiff').SectionKey;
}) {
    const [open, setOpen] = useState(false);
    return (
        <div className={'layout vertical'} style={{paddingLeft: 10}}>
            <div className={'layout horizontal gap'}
                 style={{cursor: 'pointer', userSelect: 'none', alignItems: 'center'}}
                 onClick={() => setOpen(o => !o)}>
                <span className={`fa-solid ${open ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                      style={{fontSize: 12, width: 12}}/>
                <p>Δ {props.name}</p>
                <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 12}}>
                    ({props.fieldChanges.length} field{props.fieldChanges.length === 1 ? '' : 's'})
                </p>
            </div>
            {open && <RecordDiffView fieldChanges={props.fieldChanges} sectionKey={props.sectionKey} identity={props.identity}/>}
        </div>
    );
}

// Row in the "Only in Other" list. Renders the record name + a small
// Resolve button that migrates the record (and its loc / image-pack
// satellites) from Other into Current. For non-metadata sections the
// button is hidden (entity migration is a follow-up).
function OnlyInOtherRow(props: {
    sectionKey: import('../../utils/branchDiff').SectionKey;
    identity: string;
    name: string;
    targetData: RemoteConstantsModel;
}) {
    const {state, executeCommand, branch, getBranchVersionNumber} = useIntegrator();
    const {getLocalizationValue} = useApp();

    const supported = isMetadataSection(props.sectionKey);
    const currentBranchLabel = `${branch} v${getBranchVersionNumber(branch)}`;

    const classification = useMemo(() => {
        if (!supported) return undefined;
        // Find the full target metadata record by uid in the matching slice.
        const slice = getTargetMetadataSlice(props.targetData, props.sectionKey);
        const targetRecord = slice.find(m => m.uid === props.identity);
        if (!targetRecord) return undefined;
        return classifyMetadataMigration(targetRecord, props.sectionKey, props.targetData, state, getLocalizationValue, currentBranchLabel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.identity, props.sectionKey, props.targetData, state, supported, currentBranchLabel]);

    return (
        <div className={'layout horizontal'} style={{paddingLeft: 10, alignItems: 'center', gap: 8}}>
            <p style={{flexGrow: 1}}>+ {props.name}</p>
            {supported && classification && (
                <button
                    title={classification.resolvable
                        ? `Migrate "${props.name}" (and its localization + image pack) from Other into Current`
                        : `Can't migrate yet:\n${(classification.missingDeps ?? []).join('\n')}`}
                    disabled={!classification.resolvable}
                    onClick={() => classification.migration && executeCommand(new MigrateMetadataRecordCommand(classification.migration))}
                    style={{
                        opacity: classification.resolvable ? 1 : 0.35,
                        cursor: classification.resolvable ? 'pointer' : 'not-allowed',
                        padding: '0 6px',
                    }}>
                    <span className={'fa-solid fa-arrow-left'} style={{fontSize: 11}}/>
                </button>
            )}
        </div>
    );
}

function getTargetMetadataSlice(target: RemoteConstantsModel, sectionKey: import('../../utils/branchDiff').SectionKey) {
    switch (sectionKey) {
        case 'unitAttributes':        return target.unitAttributes ?? [];
        case 'buildingAttributes':    return target.buildingAttributes ?? [];
        case 'techActionAttributes':  return target.techActionAttributes ?? [];
        case 'techPassiveAttributes': return target.techPassiveAttributes ?? [];
        case 'rewards':               return target.rewards ?? [];
        default: return [];
    }
}

export default SectionDiffView;
